/**
 * Default billing configuration values for order tracking.
 * Single source of truth for all billing defaults across the codebase.
 */

import { UsdDollars } from "@redotech/money/usd-amounts";
import { BillingPeriod } from "./service-invoice";

export const USD_TO_MICROS = 1_000_000;

export const ORDER_TRACKING_BILLING_DEFAULTS = {
  PERIOD: BillingPeriod.MONTHLY,
  // USD values
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  PERIOD_FEE_USD: 0 as UsdDollars,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  OVERAGE_PRICE_PER_ORDER_USD: 0.08 as UsdDollars,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  OVERAGE_PRICE_PER_SMS_USD: 0.01 as UsdDollars,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  OVERAGE_PRICE_PER_MMS_USD: 0.03 as UsdDollars,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  OVERAGE_PRICE_PER_EMAIL_USD: 0.001 as UsdDollars,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  OVERAGE_PRICE_PER_SHIPMENT_USD: 0.07 as UsdDollars,

  // Micros values (computed from USD)
  get OVERAGE_PRICE_PER_ORDER_MICROS() {
    return this.OVERAGE_PRICE_PER_ORDER_USD * USD_TO_MICROS;
  },
  get OVERAGE_PRICE_PER_SMS_MICROS() {
    return this.OVERAGE_PRICE_PER_SMS_USD * USD_TO_MICROS;
  },
  get OVERAGE_PRICE_PER_MMS_MICROS() {
    return this.OVERAGE_PRICE_PER_MMS_USD * USD_TO_MICROS;
  },
  get OVERAGE_PRICE_PER_EMAIL_MICROS() {
    return this.OVERAGE_PRICE_PER_EMAIL_USD * USD_TO_MICROS;
  },
  get OVERAGE_PRICE_PER_SHIPMENT_MICROS() {
    return this.OVERAGE_PRICE_PER_SHIPMENT_USD * USD_TO_MICROS;
  },

  // Other defaults
  INCLUDED_ORDERS: 0,
  INCLUDED_SHIPMENTS: 0,
  INCLUDED_CREDITS: 1000,
  INCLUDED_EMAILS: 1000,
} as const;

/**
 * RCS merchant prices. Credits are weighted by price over the team's SMS
 * price, so RCS draws from the same included pool as SMS and MMS.
 */
export const RCS_BILLING_DEFAULTS = {
  RICH_PRICE_MICROS: 0.01 * USD_TO_MICROS,
  RICH_MEDIA_PRICE_MICROS: 0.03 * USD_TO_MICROS,
  /** Telnyx carrier pass-throughs per brand agent. */
  AGENT_REGISTRATION_FEE_MICROS: 600 * USD_TO_MICROS,
  AGENT_MONTHLY_FEE_MICROS: 100 * USD_TO_MICROS,
} as const;

export const RECOVER_BILLING_DEFAULTS = {
  PERIOD: BillingPeriod.MONTHLY,
  PERIOD_FEE_CENTS: 0,
  REVENUE_SHARE_PERCENTAGE: 10,

  // SMS defaults (same pricing as order tracking)
  OVERAGE_PRICE_PER_SMS_MICROS:
    ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_SMS_MICROS,
  OVERAGE_PRICE_PER_MMS_MICROS:
    ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_MMS_MICROS,
  INCLUDED_CREDITS: ORDER_TRACKING_BILLING_DEFAULTS.INCLUDED_CREDITS,
} as const;
